import { Search } from "@/components/ui";
import { SearchIcon } from "lucide-react";
import { useMemo, useState } from "react";
import {
  type ChartingCondition,
  useChartingConditionSearch,
} from "../network/useChartingConditionSearch";

interface ChartingConditionSearchProps {
  id?: string;
  patientId: string;
  motherEncounterId: string;
  onConditionSelected: (condition: ChartingCondition | undefined) => void;
  isLoading?: boolean;
  disabled?: boolean;
  "aria-invalid"?: boolean;
  "aria-label"?: string;
  pendingConditionCodes?: ReadonlySet<string>;
  clearOnSelect?: boolean;
  placeholder?: string;
}

export const ChartingConditionSearch = ({
  id,
  patientId,
  motherEncounterId,
  onConditionSelected,
  isLoading = false,
  disabled = false,
  "aria-invalid": ariaInvalid,
  "aria-label": ariaLabel,
  pendingConditionCodes,
  clearOnSelect = false,
  placeholder = "Search for a condition",
}: ChartingConditionSearchProps) => {
  const [input, setInput] = useState("");
  const {
    data: conditionsData,
    isDebouncing,
    isFetching,
  } = useChartingConditionSearch({
    patientId,
    searchTerm: input,
    motherEncounterId,
  });

  const isSearching = isDebouncing || isFetching;

  const options = useMemo(
    () =>
      conditionsData
        ?.map((condition) => {
          const isPending = pendingConditionCodes?.has(condition.conditionCode);
          const disabled = Boolean(condition.existingConditionId || isPending);
          const endLabel = isPending ? "Adding…" : undefined;
          const label = condition.conditionDisplay;
          const value = condition.conditionCode;
          let subLabel: string | undefined;

          if (condition.existingConditionId) {
            subLabel = "(Already on Visit)";
          } else if (condition.existingEpisodeOfCareId) {
            subLabel = "(From Medical History)";
          }

          return {
            disabled,
            endLabel,
            label,
            subLabel,
            value,
          };
        })
        .sort((a, _) => (a.subLabel ? -1 : 1)),
    [conditionsData, pendingConditionCodes],
  );

  return (
    <Search
      id={id}
      aria-invalid={ariaInvalid}
      aria-label={ariaLabel}
      showLoadingSpinner
      clearOnSelect={clearOnSelect}
      leftIcon={SearchIcon}
      placeholder={placeholder}
      options={options || []}
      isLoading={isSearching || isLoading}
      disabled={disabled}
      minLength={3}
      onInputChange={setInput}
      onItemSelected={(item) =>
        onConditionSelected(
          conditionsData?.find(
            (condition) => condition.conditionCode === item.value,
          ),
        )
      }
    />
  );
};
